import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { AlertService, EnvironmentsService } from '@out-web/core';
import { ReCaptchaV3Service, RecaptchaV3Module } from 'ng-recaptcha';
import { AlertComponent, LabelComponent } from '@out-web/components';

@Component({
  selector: 'out-faq',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LabelComponent,
    RecaptchaV3Module,
    AlertComponent,
  ],
  templateUrl: './faq.component.html',
  styleUrl: './faq.component.scss',
})
export class FaqComponent implements OnInit {
  ngOnInit() {
    this.setMetaData();
    this.setupValueChangeListeners();
    this.listenToMessageFieldValidation();
  }

  private hasRecaptchaTriggered = false;
  faqForm: FormGroup;
  formErrors: string[] = [];
  isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    @Inject(EnvironmentsService)
    private environmentsService: EnvironmentsService,
    private http: HttpClient,
    private alertService: AlertService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private meta: Meta,
    private titleService: Title
  ) {
    this.faqForm = this.fb.group({
      firstName: ['', [Validators.required, this.noWhitespaceValidator]],
      lastName: ['', [Validators.required, this.noWhitespaceValidator]],
      telephone: [''],
      email: ['', [Validators.required, Validators.email]],
      message: [
        '',
        [
          Validators.required,
          this.noWhitespaceValidator,
          Validators.minLength(30),
        ],
      ],
      Recaptcha: ['', [Validators.required]],
    });
  }

  noWhitespaceValidator(control: FormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }

  // Setup value change listeners for each form control
  setupValueChangeListeners() {
    Object.keys(this.faqForm.controls).forEach((key) => {
      this.faqForm.get(key)?.valueChanges.subscribe(() => {
        this.clearFieldError(key);
      });
    });
  }

  // Check for form validity on submission
  async onSubmit() {
    if (this.faqForm.valid) {
      this.isSubmitting = true;

      try {
        await this.http
          .post(
            `${this.environmentsService.url}/Notifications/Contact`,
            this.faqForm.value
          )
          .toPromise();
        this.alertService.showAlert({
          title: 'Message Sent Successfully',
          message:
            'Your message has been successfully sent. We will get back to you as soon as possible.',
          type: 'success',
          duration: 5000,
          position: 'top-right',
        });
      } catch (error) {
        this.alertService.showAlert({
          title: 'Message Not Sent',
          message:
            'An error occurred while sending your message. Please try again later.',
          type: 'error',
          duration: 5000,
          position: 'top-right',
        });
        console.error('Submission error:', error);
      } finally {
        this.isSubmitting = false;
      }
    } else {
      this.faqForm.markAllAsTouched();
      this.updateFormErrors();

      window.scrollTo(0, 0);
    }
  }

  executeRecaptcha(action: string): void {
    this.recaptchaV3Service.execute(action).subscribe((token) => {
      this.faqForm.controls['Recaptcha'].setValue(token);
    });
  }

  listenToMessageFieldValidation(): void {
    const messageControl = this.faqForm.get('message');

    if (messageControl) {
      messageControl.statusChanges.subscribe(() => {
        if (messageControl.valid && !this.hasRecaptchaTriggered) {
          this.executeRecaptcha('form');
          this.hasRecaptchaTriggered = true; // Prevent further triggers
        }
      });
    }
  }

  // Function to update form errors
  updateFormErrors() {
    this.formErrors = [];
    Object.keys(this.faqForm.controls).forEach((key) => {
      const controlErrors = this.faqForm.get(key)?.errors;
      if (controlErrors) {
        if (controlErrors['required']) {
          this.formErrors.push(
            `${key
              .replace(/([A-Z])/g, ' $1')
              .toLowerCase()
              .replace(/^([a-z])/, (str) => str.toUpperCase())} is not valid.`
          );
        } else if (controlErrors['email']) {
          this.formErrors.push(`Email is not valid.`);
        }
      }
    });
  }

  // Function to clear the error message for a specific field
  clearFieldError(fieldName: string) {
    const control = this.faqForm.get(fieldName);
    if (control && control.valid) {
      // Remove the specific field's error from formErrors
      this.formErrors = this.formErrors.filter(
        (error) =>
          !error
            .toLowerCase()
            .includes(fieldName.replace(/([A-Z])/g, ' $1').toLowerCase())
      );
    }
  }

  setMetaData() {
    this.titleService.setTitle(
      'Frequently Asked Questions | Sell Your Tesla | Only Used Tesla'
    );
    this.meta.addTags([
      {
        name: 'description',
        property: 'description',
        content:
          'Frequently Asked Questions | Only Used Tesla | A Place Exclusively Dedicated To Buying And Selling Used Teslas',
        vmid: 'description',
      },
      {
        name: 'og:description',
        property: 'og:description',
        content:
          'Frequently Asked Questions | Only Used Tesla | A Place Exclusively Dedicated To Buying And Selling Used Teslas',
        hid: 'og:description',
      },
      {
        name: 'keywords',
        content:
          'Used Tesla, Buy Tesla Canada, Tesla Model 3, Tesla Model S, Used Electric Cars, Affordable Tesla, Certified Tesla',
      },
      {
        name: 'author',
        content: 'Only Used Tesla',
      },
    ]);
  }
}
